import React, { useState, useEffect } from "react";
import { cx } from "utils/join-classnames";
import subtitleExportOptions from "core/constants/captionsExport";
import {
    Modal,
    Dropdown,
    TextField,
    TextArea,
    TagInput,
    Tooltip,
} from "ui/components";
import { usePublishContext } from "ui/context/PublishContext";
import { useSessionContext } from "ui/context/SessionContext";
import { preparePublish } from "core/services/site/fragment";
import { Fragment, SubtitleExportValue } from "core/models";
import styles from "./ShareYoutubeModal.module.scss";

interface Props {
    handleClose(): void;
    handleShare(
        selectedAccount: string,
        categoryid: number,
        embeddable: boolean,
        privacystatus: string,
        title: string,
        description: string,
        tags: string[],
        subtitleExport: SubtitleExportValue,
    ): void;
    fragment: Fragment;
}

interface YtCategory {
    id: string;
    name: string;
}

const fetchYtCats = async (
    accessToken: string,
    fragmentId: number,
    publisherKey: string,
): Promise<YtCategory[]> => {
    try {
        const youtubeCatsResult = await preparePublish<YtCategory[]>(
            accessToken,
            fragmentId,
            publisherKey,
            "listcategories",
        );
        if (youtubeCatsResult.error || !youtubeCatsResult.value) {
            throw new Error("get YT cats call failed");
        }
        return youtubeCatsResult.value;
    } catch (err) {
        console.error(err);
        return [];
    }
};

const ShareYoutubeModal: React.FC<Props> = ({
    handleClose,
    handleShare,
    fragment,
}: Props) => {
    const publishContext = usePublishContext();
    const sessionContext = useSessionContext();

    const youtubeAccounts = publishContext.activeYoutubeAccounts.map(
        (account) => ({
                id: account.key,
                title: account.name,
                value: account.key,
            }),
    );

    const [selectedChannel, setSelectedChannel] = useState<string | undefined>(
        publishContext.activeYoutubeAccounts[0].key,
    );
    const [subtitleOption, setSubtitleOption] = useState<SubtitleExportValue>();
    const [videoTitle, setVideoTitle] = useState<string | undefined>(
        fragment.title || "",
    );
    const [videoTags, setTags] = useState<string[] | undefined>(fragment.tags);
    const [videoPrivacy, setPrivacy] = useState<string | undefined>("unlisted");
    const [videoCategory, setCategory] = useState<number | undefined>(1);
    const [videoEmbed, setEmbed] = useState<boolean>(true);
    const [videoDesc, setVideoDesc] = useState<string>("");
    const [categories, setCategories] = useState<YtCategory[]>([]);

    const getCats = async (): Promise<void> => {
        if (!sessionContext.session) {
            throw Error("no session");
        }
        const result = await fetchYtCats(
            sessionContext.session.token,
            fragment.id,
            publishContext.activeYoutubeAccounts[0].key,
        );
        setCategories(result);
        setCategory(parseInt(result[0].id, 10));
    };
    useEffect(() => {
        getCats();
    }, []);

    const optionsEmbed = [
        {
            id: 0,
            title: "Allow embedding",
            value: "true",
        },
        {
            id: 1,
            title: "Disallow embedding",
            value: "false",
        },
    ];
    const optionsPrivacy = [
        {
            id: 0,
            title: "Unlisted",
            value: "unlisted",
        },
        {
            id: 1,
            title: "Private",
            value: "private",
        },
        {
            id: 21,
            title: "Public",
            value: "public",
        },
    ];
    const optionsCategories = categories.map((cat) => ({
            id: cat.id,
            title: cat.name,
            value: cat.id.toString(),
        }));

    const handleDescChange = (value: string) => setVideoDesc(value);

    const handleShareClick = () => {
        if (
            !selectedChannel ||
            !videoCategory ||
            !videoPrivacy ||
            !videoTitle ||
            !videoTags
        ) {
            return;
        }
        handleShare(
            selectedChannel,
            videoCategory,
            videoEmbed,
            videoPrivacy,
            videoTitle,
            videoDesc,
            videoTags,
            subtitleOption,
        );
    };
    const handleSubtitleOptionChange = (val: SubtitleExportValue): void => {
        setSubtitleOption(val);
    };

    const modalContent = (
        <div className={styles.container}>
            <div className={styles.form}>
                <div className={styles.column__left}>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Post to
                        </div>
                        <Dropdown
                            theme="dark"
                            options={youtubeAccounts}
                            onValueChanged={setSelectedChannel}
                            value={selectedChannel}
                        />
                    </div>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Video Title
                        </div>
                        <TextField
                            theme="dark"
                            onValueChanged={(value) => {
                                setVideoTitle(value);
                            }}
                            value={videoTitle || ""}
                            defaultText="Video title"
                        />
                    </div>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Description
                        </div>
                        <TextArea
                            onValueChanged={handleDescChange}
                            placeholder="Tell your audiences about the video"
                        />
                    </div>
                    <div className={styles.formelement}>
                        <Tooltip
                            text="No captions for this clip"
                            disabled={fragment.hasSubtitles()}
                        >
                            <div className={styles.formelement__caption}>
                                Captions
                            </div>
                            <Dropdown
                                theme="dark"
                                options={subtitleExportOptions}
                                onValueChanged={handleSubtitleOptionChange}
                                value={subtitleOption}
                                disabled={!fragment.hasSubtitles()}
                            />
                        </Tooltip>
                    </div>
                </div>
                <div
                    className={cx(
                        styles.column__right,
                        styles.publish__column__right,
                    )}
                >
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Privacy
                        </div>
                        <Dropdown
                            theme="dark"
                            options={optionsPrivacy}
                            onValueChanged={setPrivacy}
                            value={videoPrivacy}
                        />
                    </div>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>Embed</div>
                        <Dropdown
                            theme="dark"
                            options={optionsEmbed}
                            onValueChanged={(value) =>
                                setEmbed(value === "true")
                            }
                            value={videoEmbed.toString()}
                        />
                    </div>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Category
                        </div>
                        <Dropdown
                            theme="dark"
                            options={optionsCategories}
                            onValueChanged={(value) =>
                                setCategory(
                                    value ? parseInt(value, 10) : undefined,
                                )
                            }
                            value={videoCategory?.toString(10)}
                        />
                    </div>
                    <div className={styles.dropdown}>
                        <div className={styles.formelement__caption}>Tags</div>
                        <TagInput
                            value={videoTags || []}
                            onChange={(tags) => setTags(tags)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            content={modalContent}
            lineColor="youtube"
            theme="dark"
            onSave={handleShareClick}
            saveButtonDisabled={
                !selectedChannel ||
                !videoCategory ||
                !videoPrivacy ||
                !videoTitle ||
                !videoTags
            }
            onClose={handleClose}
            saveButtonText="Share"
            title={`Share "${fragment.title}" to Youtube`}
            icon="youtube-original"
        />
    );
};

export default ShareYoutubeModal;
